import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../../../components/common/button";
import { Input } from "../../../../../../components/common/input";
import { Text } from "../../../../../../components/common/typography";
import { authOtpService } from "../../../../../api_client";
import { authSession } from "../../../../token_store";
import { useMe } from "../../../../../../swr";
import { handleAxiosError } from "../../../../../../common/error_handling";
import { AppRoutes } from "../../../../../../common/routes/app_routes";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../utils/toasts";
import { useRegisterWizard } from "../../../../domain/register-wizard";
import RegisterWizardLayout from "./register-wizard-layout";
import {
  OTP_LENGTH,
  isCompleteOtp,
  sanitizeOtp,
} from "../../../../../../utils/validation";

export function VerifyPhoneStep() {
  const navigate = useNavigate();
  const { phoneNumber, registered } = useRegisterWizard();
  const { mutate } = useMe();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isCompleteOtp(code) || submitting) return;
    if (!registered) {
      showErrorToast("Please complete business details first.");
      navigate(AppRoutes.CREATE_ACCOUNT_BUSINESS_DETAILS);
      return;
    }
    setSubmitting(true);
    try {
      const resp = await authOtpService.otpVerifyComplete({
        phoneNumber,
        otp: code,
      });
      authSession.setAccessToken(resp.data.accessToken);
      await mutate();
      navigate(AppRoutes.CREATE_ACCOUNT_DOCUMENTS);
    } catch (error) {
      showErrorToast(handleAxiosError(error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    if (resending) return;
    setResending(true);
    try {
      await authOtpService.otpResendVerification({ phoneNumber });
      showSuccessToast("A new code has been sent.");
    } catch (error) {
      showErrorToast(handleAxiosError(error).message);
    } finally {
      setResending(false);
    }
  };

  return (
    <RegisterWizardLayout
      title="Verify phone number"
      onBack={() => navigate(AppRoutes.CREATE_ACCOUNT_BUSINESS_DETAILS)}
    >
      <Text variant="body" tone="body" className="text-center">
        To verify your phone, we sent you a One Time Password to{" "}
        <Text variant="bodyBold" tone="dark" inline>
          {phoneNumber || "your phone"}
        </Text>
      </Text>

      <form onSubmit={submit} className="flex w-full flex-col gap-6">
        <Input
          label="Security code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={`Enter the ${OTP_LENGTH}-digit code`}
          value={code}
          onChange={(e) => setCode(sanitizeOtp(e.target.value))}
          required
        />
        <Button
          variant="primary"
          type="submit"
          fullWidth
          disabled={!isCompleteOtp(code) || submitting}
        >
          {submitting ? "Verifying…" : "Continue"}
        </Button>
        <div className="flex w-full items-center justify-center">
          <Text variant="body" tone="muted" inline>
            Didn't get a code?{" "}
            <button
              type="button"
              onClick={resend}
              disabled={resending}
              className="bg-transparent disabled:opacity-50"
            >
              <Text variant="bodyBold" tone="dark" inline>
                {resending ? "Sending…" : "Resend"}
              </Text>
            </button>
          </Text>
        </div>
      </form>
    </RegisterWizardLayout>
  );
}

export default VerifyPhoneStep;
